import { useRef } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import { Printer, ArrowLeft, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { QuizScript } from '@quebon/shared-learning'
import {
  QuizRenderer,
  PAPER_SIZES,
  mmToPx,
  toQuizData,
  PageFooter,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'

// ─── Constants ───────────────────────────────────────────────────────

const CIRCLE_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']
const REFERENCE_TYPES = new Set(['pairing', 'image_select'])

// ─── Answer Text Extraction ──────────────────────────────────────────

function getAnswerText(quiz: QuizScript): string {
  const { type, correctAnswers } = quiz
  if (!correctAnswers || correctAnswers.length === 0) return '-'

  switch (type) {
    case 'multi_choice': {
      // correctAnswers는 인덱스(1-based) 또는 key
      return correctAnswers
        .map(a => {
          const idx = typeof a === 'number' ? a : parseInt(String(a), 10)
          if (!isNaN(idx) && idx >= 1 && idx <= CIRCLE_NUMBERS.length) {
            return CIRCLE_NUMBERS[idx - 1]
          }
          return String(a)
        })
        .join(', ')
    }
    case 'boolean':
      return String(correctAnswers[0]) === 'true' ? '맞아요' : '틀려요'
    case 'pairing':
    case 'image_select':
      return '해설참조'
    case 'fill_blank':
    case 'drag_text':
    case 'counting':
    case 'coloring':
    case 'select_text':
    case 'ordering':
    case 'explanation':
    case 'short_answer':
      return correctAnswers.map(a => String(a)).join(', ')
    default:
      return correctAnswers.map(a => String(a)).join(', ')
  }
}

function isReferenceType(type: string): boolean {
  return REFERENCE_TYPES.has(type)
}

// ─── Sub-Components ──────────────────────────────────────────────────

function AnswerSection({ quizzes }: { quizzes: QuizScript[] }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {/* 정답 헤더 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #e8b4c8 0%, #d4899f 100%)',
          padding: '6px 16px',
          borderRadius: '4px 4px 0 0',
          marginBottom: 0,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>정답</span>
      </div>
      {/* 정답 그리드 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          border: '1px solid #d4899f',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          overflow: 'hidden',
        }}
      >
        {quizzes.map((quiz, idx) => (
          <div
            key={quiz.scriptId || idx}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              lineHeight: '18px',
              borderBottom: idx < quizzes.length - (quizzes.length % 3 || 3) ? '1px solid #f0d0de' : 'none',
              borderRight: (idx + 1) % 3 !== 0 ? '1px solid #f0d0de' : 'none',
            }}
          >
            <span style={{ fontWeight: 700, marginRight: 6 }}>{idx + 1}.</span>
            <span>{getAnswerText(quiz)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ExplanationSection({ quizzes }: { quizzes: QuizScript[] }) {
  return (
    <div>
      {/* 해설 헤더 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #a8d5a2 0%, #7fbf7a 100%)',
          padding: '6px 16px',
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>해설</span>
      </div>
      {/* 해설 목록 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {quizzes.map((quiz, idx) => (
          <ExplanationItem key={quiz.scriptId || idx} quiz={quiz} number={idx + 1} />
        ))}
      </div>
    </div>
  )
}

function ExplanationItem({ quiz, number }: { quiz: QuizScript; number: number }) {
  const descText = quiz.description?.text
  const hasDescription = !!descText
  const isRefType = isReferenceType(quiz.type)

  return (
    <div style={{ fontSize: 13, lineHeight: '22px' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <span style={{ fontWeight: 700, minWidth: 20, flexShrink: 0 }}>{number}.</span>
        <div style={{ flex: 1 }}>
          {/* 정답 요약 (해설참조가 아닌 경우) */}
          {!isRefType && (
            <span style={{ fontWeight: 600, color: '#b45309', marginRight: 8 }}>
              {getAnswerText(quiz)}
            </span>
          )}

          {/* 해설 텍스트 */}
          {hasDescription && (
            <span style={{ whiteSpace: 'pre-wrap' }}>{descText}</span>
          )}

          {/* 해설참조 유형: 정답 처리된 문제 UI */}
          {isRefType && (
            <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 4, padding: 8 }}>
              <QuizRenderer
                mode="print"
                quizType={quiz.type}
                quizId={quiz.quizId || quiz.scriptId || ''}
                quiz={toQuizData(quiz)}
                number={number}
                compact={true}
              />
            </div>
          )}

          {/* 해설 없는 경우 */}
          {!hasDescription && !isRefType && (
            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>해설 없음</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────

export function AnswerKeyPreviewPage() {
  const { lessonId } = useParams<{ bookId: string; lessonId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)

  const lessonName = searchParams.get('name') || '레슨'
  const startPage = parseInt(searchParams.get('startPage') || '1', 10)
  const bookLabel = searchParams.get('bookLabel') || ''
  const quetureTitle = searchParams.get('quetureTitle') || lessonName

  // Fetch quizzes with answers + correctAnswers
  const { data: quizzes = [], isLoading } = useQuery({
    queryKey: ['answer-key-quizzes', lessonId],
    queryFn: () =>
      quizScriptApi.getContentQuizzes(
        Number(lessonId),
        undefined,     // all locations
        true,          // showAnswer
        true,          // useFixedVars
        true           // includeSolveResult
      ),
    enabled: !!lessonId,
  })

  // Paper setup
  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${bookLabel}_${lessonName}_정답해설`,
    pageStyle: `
      @page { size: ${paper.width}mm ${paper.height}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; position: relative !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar Controls */}
      <div className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-200 flex flex-col p-4 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft size={16} /> 뒤로가기
        </button>

        <div className="mb-4">
          <h2 className="text-sm font-semibold text-slate-900 mb-1">정답 & 해설</h2>
          <p className="text-xs text-slate-500 truncate">{quetureTitle}</p>
          <p className="text-xs text-slate-400 mt-1">시작 페이지: {startPage}</p>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Loader2 size={14} className="animate-spin" /> 로딩 중...
          </div>
        )}

        <div className="text-xs text-slate-500 mb-4">
          총 {quizzes.length}문제
        </div>

        <button
          onClick={() => reactToPrintFn()}
          disabled={quizzes.length === 0 || isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
        >
          <Printer size={16} /> PDF 다운로드
        </button>
      </div>

      {/* Preview Area */}
      <div className="ml-64 p-8 flex justify-center">
        {isLoading ? (
          <div className="flex items-center gap-3 text-slate-500">
            <Loader2 className="animate-spin" /> 퀴즈 데이터 로딩 중...
          </div>
        ) : quizzes.length === 0 ? (
          <div className="text-slate-400 text-sm">퀴즈가 없습니다.</div>
        ) : (
          <div
            ref={printRef}
            className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
            style={{ width: `${pageWidthPx}px`, lineHeight: 1.6 }}
          >
            <style>{`
              .print-page { overflow: hidden; box-sizing: border-box; min-height: ${pageHeightPx}px; position: relative; }
            `}</style>

            {/* Page 1: 정답 + 해설 */}
            <div
              className="print-page"
              style={{
                padding: '32px 28px 48px',
                position: 'relative',
              }}
            >
              {/* 레슨 제목 */}
              <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '2px solid #1e293b' }}>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                  {quetureTitle}
                </h1>
                <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
                  정답과 해설 · p.{startPage}
                </p>
              </div>

              {/* 정답 영역 */}
              <AnswerSection quizzes={quizzes} />

              {/* 해설 영역 */}
              <ExplanationSection quizzes={quizzes} />

              {/* Page Footer */}
              <PageFooter
                pageNumber={1}
                isEven={false}
                bookLabel={bookLabel}
                footerTop={pageHeightPx - 36}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
